import { useTranslation } from 'react-i18next';
import { StyledAdminEditGroupPoolConfigs } from './styles';
import { RootState, useAppDispatch, useAppSelector } from 'store/store';
import { useSelector } from 'react-redux';
import { Button, DisplayItemsPerPageSelect, Paginator, SecondaryButton } from 'components';
import { AdminList } from '../AdminList';
import {
  groupPoolConfigsListSetPage,
  selectAdminGroupPoolConfigs,
  setItemsPerGroupPoolConfigsListPage,
} from 'store/slices/adminGroupPoolConfigsSlice';
import {fetchGroupPoolConfigs, fetchGroupPoolConfigsAll} from 'store/thunks/adminGroupPoolConfigsThunk';
import { RefreshButton } from '../RefreshButton';
import { AdminGroupPoolConfigsListHeader } from '../AdminGroupPoolConfigsListHeader';
import { AdminGroupPoolConfigsListRow } from '../AdminGroupPoolConfigsListRow';
import { useEffect, useState, useMemo, useRef } from 'react';
import { AdminConfirmModal } from '../AdminConfirmModal';
import { AdminAddModal } from '../AdminAddModal';
import { AdminAddGroupPoolConfigForm } from '../AdminAddGroupPoolConfigForm';
import { IGroupPoolConfig } from 'types';
import { AdminGroupsSelect } from '../AdminGroupsSelect';
import { selectAdminDeviceGroups } from 'store/slices/adminDeviceGroupsSlice';
import {
  VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_MAX_ATTEMPTS_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_PASSWORD_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_PRIORITY_REQUIRED,
  VALIDATION_ERROR_ADMIN_POOL_USER_REQUIRED,
} from 'constants/errorsConstants';
import { AdminRepository } from 'repositories/AdminRepository';
import { EApiResponseStatus } from 'types/api';
import { IAdminUpdatePoolConfigResponse } from 'types';

export interface IGroupPoolConfigDraft {
  poolConfig: number | null;
  priority: string;
  maxAttempts: string;
}

export const AdminEditGroupPoolConfigs = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { groupPoolConfigs } = useSelector(selectAdminGroupPoolConfigs);
  const { groupPoolConfigsAll } = useSelector(selectAdminGroupPoolConfigs);
  const { normalizedGroups } = useSelector(selectAdminDeviceGroups);
  const { token } = useAppSelector((state) => state.auth);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addFormState, setAddFormState] = useState<IGroupPoolConfigDraft>({
    poolConfig: null,
    priority: '',
    maxAttempts: '',
  });
  const [editedConfigs, setEditedConfigs] = useState<IGroupPoolConfig[]>(groupPoolConfigs);
  const [selectedDeviceGroup, setSelectedDeviceGroup] = useState<IGroupPoolConfig['id']>(1);
  const [isSavingOrder, setIsSavingOrder] = useState(false);
  const [hasLocalEdit, setHasLocalEdit] = useState(false);
  const [isSyncingAfterSave, setIsSyncingAfterSave] = useState(false);
  const prevSelectedGroupRef = useRef<IGroupPoolConfig['id'] | null>(null);
  const { pagesQuantity, currentPage, itemsPerPage } = useSelector(
    (state: RootState) => state.adminGroupPoolConfigs,
  );

  const closeAddModal = () => {
    setShowAddModal(false);
  };

  const submitAddPoolConfigHandler = async () => {
    setShowAddModal(true);
  };

  const submitAddAction = async () => {
    submitAddPoolConfig();
    setShowAddModal(false);
  };

  const submitAddPoolConfig = async () => {
    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof selectedDeviceGroup !== 'number') throw Error('no selected device group');

      const { poolConfig, priority, maxAttempts } = addFormState;

      if (typeof poolConfig !== 'number') throw Error(VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED);
      if (!priority.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_PRIORITY_REQUIRED);
      if (!maxAttempts.trim()) throw Error(VALIDATION_ERROR_ADMIN_POOL_MAX_ATTEMPTS_REQUIRED);

      const pool = groupPoolConfigsAll.find((config) => config.id === poolConfig);

      if (!pool) {
        throw new Error(`pool config with ID ${poolConfig} not found`);
      }

      const updatePoolConfigData = {
        ...pool,
        priority: parseInt(priority),
        maxAttempts: parseInt(maxAttempts),
        deviceGroupId: selectedDeviceGroup,
      };

      const updatePoolConfigResponse = await AdminRepository.setPoolConfig(
        token,
        updatePoolConfigData,
      );

      if (updatePoolConfigResponse.status === EApiResponseStatus.FAILURE) {
        throw new Error(updatePoolConfigResponse.error);
      }

      await dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
      setHasLocalEdit(false);
    } catch (error) {
      console.error('submit add pool config in group error', error);
    }
  };

  const submitDeletePoolConfig = async (poolId: number) => {
    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof poolId !== 'number') throw Error(VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED);

      const pool = groupPoolConfigs.find((config) => config.id === poolId);

      if (!pool) {
        throw new Error(`pool config with ID ${poolId} not found`);
      }

      const response = await AdminRepository.deletePoolConfig(token, poolId);

      if (response.status === EApiResponseStatus.FAILURE) {
        throw new Error(response.error);
      }

      if (selectedDeviceGroup) {
        await dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
      }
    } catch (error) {
      console.error('submit delete pool config error', error);
    }
  };

  const submitDeletePoolConfigFromGroup = async (poolId: number, groupId: number) => {
    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof poolId !== 'number') throw Error(VALIDATION_ERROR_ADMIN_POOL_ID_REQUIRED);

      const pool = groupPoolConfigs.find((config) => config.id === poolId);

      if (!pool) {
        throw new Error(`pool config with ID ${poolId} not found`);
      }

      const response = await AdminRepository.deletePoolConfigFromGroup(token, poolId, groupId);

      if (response.status === EApiResponseStatus.FAILURE) {
        throw new Error(response.error);
      }

      if (selectedDeviceGroup) {
        await dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
        await dispatch(fetchGroupPoolConfigsAll);
      }
    } catch (error) {
      console.error('submit delete pool config error', error);
    }
  };

  const submitChangeOrderPoolConfig = async (id: number, direction: 'up' | 'down') => {
    let previousConfigs: IGroupPoolConfig[] = [];
    let updatedConfigs: IGroupPoolConfig[] = [];

    setEditedConfigs((prev) => {
      previousConfigs = [...prev];
      const sorted = [...prev].sort((a, b) => a.priority - b.priority);
      const index = sorted.findIndex((c) => c.id === id);
      if (index === -1) return prev;

      const targetIndex = direction === 'up' ? index - 1 : index + 1;
      if (targetIndex < 0 || targetIndex >= sorted.length) return prev;

      [sorted[index], sorted[targetIndex]] = [sorted[targetIndex], sorted[index]];

      updatedConfigs = sorted.map((c, i) => ({
        ...c,
        priority: i + 1,
      }));

      setHasLocalEdit(true);
      return updatedConfigs;
    });

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof selectedDeviceGroup !== 'number') throw Error('no selected device group');

      setIsSavingOrder(true);

      const requests = updatedConfigs
        .map((config) => {
          const previousConfig = previousConfigs.find((prev) => prev.id === config.id);
          if (previousConfig && previousConfig.priority !== config.priority) {
            return AdminRepository.setPoolConfig(token, config);
          }
          return null;
        })
        .filter((req): req is Promise<IAdminUpdatePoolConfigResponse> => req !== null);

      if (requests.length === 0) {
        setIsSavingOrder(false);
        return;
      }

      const responses = await Promise.all(requests);

      const failedResponse = responses.find((res) => res.status === EApiResponseStatus.FAILURE);
      if (failedResponse) {
        setEditedConfigs(previousConfigs);
        setIsSavingOrder(false);
        throw new Error(failedResponse.error);
      }

      setIsSavingOrder(false);

      setIsSyncingAfterSave(true);
      setTimeout(async () => {
        await dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
        setIsSyncingAfterSave(false);
      }, 1000);
    } catch (error) {
      console.error('submit change priority of pool config in group error', error);
      setIsSavingOrder(false);
    }
  };

  const handleConfigChange = (updatedConfig: IGroupPoolConfig) => {
    setEditedConfigs((prev) =>
      prev.map((item) => (item.id === updatedConfig.id ? updatedConfig : item)),
    );
    setHasLocalEdit(true);
  };

  const handleDelete = async (id: number, groupId: number) => {
    await submitDeletePoolConfigFromGroup(id, groupId);
    setHasLocalEdit(false);
  };

  const handleSave = async () => {
    if (editedConfigs.length === 0) return;

    try {
      if (!token) throw Error('no token or unathorised');
      if (typeof selectedDeviceGroup !== 'number') throw Error('no selected device group');

      const requests = editedConfigs
        .map((config) => {
          const poolConfigItem = groupPoolConfigs.find((item) => item.id === config.id);
          if (!poolConfigItem) throw new Error(`Pool config onfig with ID ${config.id} not found`);

          const hasChanges = JSON.stringify(config) !== JSON.stringify(poolConfigItem);
          return hasChanges ? config : null;
        })
        .filter(Boolean)
        .map((config) => AdminRepository.setPoolConfig(token, config!));

      if (requests.length === 0) return;

      const responses = await Promise.all(requests);

      const failedResponse = responses.find((res) => res.status === EApiResponseStatus.FAILURE);
      if (failedResponse) throw new Error(failedResponse.error);

      await dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
      await dispatch(fetchGroupPoolConfigsAll);

      setHasLocalEdit(false);
    } catch (error) {
      console.error('submit group pool config save changes error', error);
    }
  };

  const refreshButtonHandler = async () => {
    if (selectedDeviceGroup) {
      dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
    }
  };

  const closeConfirmModal = () => {
    setShowConfirmModal(false);
  };

  const submitDiscardChangesActionHandler = () => {
    setShowConfirmModal(true);
  };

  const submitDiscardChangesAction = async () => {
    setEditedConfigs([...groupPoolConfigs]);
    setHasLocalEdit(false);
    setShowConfirmModal(false);
  };

  const changeCurrentPageHandler = (currentPage: number) => {
    dispatch(groupPoolConfigsListSetPage(currentPage));
    if (selectedDeviceGroup) {
      dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
    }
  };

  const changeItemsPerPageQuantityHandler = (itemsQuantity: number) => {
    dispatch(setItemsPerGroupPoolConfigsListPage(itemsQuantity));
    if (selectedDeviceGroup) {
      dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
    }
  };

  const selectGroupHandler = (groupId: IGroupPoolConfig['id']) => {
    setSelectedDeviceGroup(groupId);
    dispatch(fetchGroupPoolConfigs(groupId));
  };

  useEffect(() => {
    if (selectedDeviceGroup) {
      dispatch(fetchGroupPoolConfigs(selectedDeviceGroup));
    }
  }, []);

  useEffect(() => {
    if (isSavingOrder || isSyncingAfterSave) return;

    const groupChanged = prevSelectedGroupRef.current !== selectedDeviceGroup;

    if (groupChanged || !hasLocalEdit) {
      setEditedConfigs(
          groupPoolConfigs.map(config => ({
            ...config,
            deviceGroupId: selectedDeviceGroup,
          })),
      );

      prevSelectedGroupRef.current = selectedDeviceGroup;
      setHasLocalEdit(false);
    }
  }, [
    groupPoolConfigs,
    selectedDeviceGroup,
    isSavingOrder,
    isSyncingAfterSave,
    hasLocalEdit,
  ]);

  const sortedConfigs = useMemo(() => {
    return [...editedConfigs].sort((a, b) => a.priority - b.priority);
  }, [editedConfigs]);

  return (
    <StyledAdminEditGroupPoolConfigs>
      <AdminList
        renderControls={() => (
          <div className="admin-edit-pool-configs-controls">
            <AdminGroupsSelect
              groups={normalizedGroups}
              currentGroupId={selectedDeviceGroup}
              onSelect={selectGroupHandler}
            />
            <Button
              title={t('adminPage.editGroupPoolConfigs.addPoolConfig')}
              onClick={submitAddPoolConfigHandler}
            />
            {showAddModal && selectedDeviceGroup !== null && (
              <AdminAddModal
                title={
                  t('adminPage.confirmModal.addGroupPoolConfig') +
                  normalizedGroups.byID[selectedDeviceGroup]?.name
                }
                onConfirm={submitAddAction}
                onCancel={closeAddModal}
                show={showAddModal}
              >
                <AdminAddGroupPoolConfigForm config={addFormState} onChange={setAddFormState} />
              </AdminAddModal>
            )}
            {hasLocalEdit && (
              <>
                <Button
                  title={t('adminPage.editGroupPoolConfigs.saveChanges')}
                  onClick={handleSave}
                />
                <div className="admin-edit-pool-configs-changes-wrapper">
                  <SecondaryButton
                    title={t('adminPage.editGroupPoolConfigs.discardChanges')}
                    outline
                    onClick={submitDiscardChangesActionHandler}
                  />
                  <div className="admin-edit-pool-configs-changes-badge">
                    {t('adminPage.editGroupPoolConfigs.unsavedChanges')}
                  </div>
                </div>
              </>
            )}
            {showConfirmModal && (
              <AdminConfirmModal
                title={t('adminPage.confirmModal.discardChanges')}
                onConfirm={submitDiscardChangesAction}
                onCancel={closeConfirmModal}
                show={showConfirmModal}
              />
            )}
            <div className="admin-edit-pool-configs-controls__right">
              <RefreshButton onClick={refreshButtonHandler} />
            </div>
          </div>
        )}
      >
        <div className="admin-edit-pool-configs-list-body">
          <AdminGroupPoolConfigsListHeader />
          {selectedDeviceGroup !== null &&
            sortedConfigs.map((config) => (
              <AdminGroupPoolConfigsListRow
                key={config.id}
                config={config}
                onChange={handleConfigChange}
                onChangeOrder={(id, newPriority) => {
                  const currentConfig = editedConfigs.find((c) => c.id === id);
                  if (currentConfig) {
                    const direction = newPriority > currentConfig.priority ? 'up' : 'down';
                    submitChangeOrderPoolConfig(id, direction);
                  }
                }}
                onDelete={(poolId) => handleDelete(poolId, selectedDeviceGroup)}
              />
            ))}
        </div>
      </AdminList>
      {pagesQuantity > 0 && (
        <div className="list-body-pagination">
          <Paginator
            onChange={changeCurrentPageHandler}
            pageQuantity={pagesQuantity}
            currentPage={currentPage}
          />
          <DisplayItemsPerPageSelect
            onChange={changeItemsPerPageQuantityHandler}
            currentItemsPerPageQuantity={itemsPerPage}
            itemsPerPageRates={[10, 15, 25, 50]}
          />
        </div>
      )}
    </StyledAdminEditGroupPoolConfigs>
  );
};
